import { useState, useRef, useEffect } from "react";

// No changes to navConfig — all routing data lives in App.js PAGE_TO_PATH.
const navConfig = [
  { id: "home", label: "Home" },
  {
    id: "offerings", label: "Offerings",
    dropdown: [
      {
        group: "Services",
        items: [
          { id: "service-software", label: "Software Services", icon: "💻" },
          { id: "service-implementation", label: "Implementation", icon: "🔧" },
          { id: "service-geospatial", label: "Geospatial", icon: "🗺️" },
          { id: "service-managed", label: "Managed Services", icon: "☁️" },
        ],
      },
      {
        group: "Products",
        items: [
          { id: "product-ungrid", label: "Ungrid", icon: "⚡" },
          { id: "product-conduitpro", label: "ConduitPro", icon: "🔌" },
          { id: "product-electricity", label: "SLD Platform", icon: "📐" },
          { id: "product-runr", label: "rUNr", icon: "🚀" },
          { id: "product-energize", label: "Energize / De-energize", icon: "🔋" },
          { id: "product-fueerza", label: "Fueerza", icon: "👷" },
        ],
      },
    ],
  },
  {
    id: "industries", label: "Industries",
    dropdown: [
      {
        group: "Industries",
        items: [
          { id: "industry-utility", label: "Utility", icon: "⚡" },
          { id: "industry-telecom", label: "Telecom", icon: "📡" },
          { id: "industry-infrastructure", label: "Infrastructure", icon: "🏗️" },
        ],
      },
    ],
  },
  { id: "about", label: "About Us" },
  { id: "blogs", label: "Blogs" },
  {
    id: "gallery", label: "Gallery",
    dropdown: [
      {
        group: "Gallery",
        items: [
          { id: "gallery-clicks", label: "Inovaantage Clicks", icon: "📸" },
          { id: "gallery-videos", label: "Inovaantage Videos", icon: "🎬" },
          { id: "gallery-posters", label: "Inovaantage Posters", icon: "🖼️" },
          { id: "gallery-press", label: "Press Release", icon: "📰" },
        ],
      },
    ],
  },
  { id: "contact", label: "Contact Us" },
];

export default function Navbar({ activePage, setActivePage, scrolled, darkMode, toggleDarkMode }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  // Toggle body scroll lock when mobile menu opens
  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }
    return () => document.body.classList.remove("menu-open");
  }, [mobileOpen]);

  useEffect(() => {
    function handleClick(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ─── CHANGE: navigate() calls setActivePage (which is now App's navigateTo).
  // navigateTo does the pushState + React state update in one place.
  // Navbar itself needs zero knowledge of URL construction. ───────────────────
  function navigate(id) {
    setActivePage(id);   // → App.navigateTo(id) → pushState + setActivePage
    setOpenMenu(null);
    setMobileOpen(false);
  }
  // ──────────────────────────────────────────────────────────────────────────

  function isActive(item) {
    if (item.id === activePage) return true;
    if (item.dropdown) return item.dropdown.some(g => g.items.some(i => i.id === activePage));
    return false;
  }

  return (
    <nav className={"navbar" + (scrolled ? " scrolled" : "")} ref={navRef}>
      <img
        className="brand-logo-img"
        src="/dark_logo.png"
        alt="Inovaantage"
        onClick={() => navigate("home")}
      />

      <ul className={"nav-links" + (mobileOpen ? " mobile-open" : "")}>
        {navConfig.map((item) => (
          <li key={item.id} className={"nav-item" + (item.dropdown ? " has-dropdown" : "")}>
            <button
              className={"nav-btn" + (isActive(item) ? " active" : "") + (item.dropdown ? " has-arrow" : "")}
              onClick={() => {
                if (item.dropdown) {
                  if (openMenu === item.id) {
                    navigate(item.id);
                  } else {
                    setOpenMenu(item.id);
                  }
                } else {
                  navigate(item.id);
                }
              }}
            >
              {item.label}
              {item.dropdown && (
                <svg
                  className={"nav-arrow" + (openMenu === item.id ? " rotated" : "")}
                  width="12" height="12" viewBox="0 0 12 12" fill="none"
                >
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>

            {item.dropdown && openMenu === item.id && (
              <div className="nav-dropdown">
                {item.dropdown.map((group) => (
                  <div key={group.group} className="dropdown-group">
                    <p className="dropdown-group-label">{group.group}</p>
                    {group.items.map((sub) => (
                      <button
                        key={sub.id}
                        className={"dropdown-item" + (activePage === sub.id ? " active" : "")}
                        onClick={() => navigate(sub.id)}
                      >
                        <span className="dropdown-item-icon">{sub.icon}</span>
                        <span>{sub.label}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </li>
        ))}
        <li>
          <button className="nav-cta" onClick={() => navigate("contact")}>
            Request a Demo
          </button>
        </li>
      </ul>

      {/* Hamburger — rendered last so it floats right on mobile */}
      <button className="nav-hamburger" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
        <span className={"ham-line" + (mobileOpen ? " open" : "")} />
        <span className={"ham-line" + (mobileOpen ? " open" : "")} />
        <span className={"ham-line" + (mobileOpen ? " open" : "")} />
      </button>
    </nav>
  );
}
